import { Component, OnDestroy, OnInit } from '@angular/core';
import { StoreService } from 'app/ngrx/store.service';
import { Company } from 'app/modules/models/company.type';
import { MatDialog } from '@angular/material/dialog';
import { PartnersService } from 'app/shared/services/partners.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AcepActeur } from 'app/modules/models/acepActeur.type';
import { AddressAutocompleteComponent } from 'app/shared/address-autucomplete/address-autucomplete.component';
import { AddressFeature, FeatureCollection } from 'app/modules/models/stateAdress.type';
import { BuildingService } from 'app/modules/customer-details/building/building.service';
import { debounceTime } from 'rxjs';
import { CreationProjetService } from './creation-projet.service';
import { UploadSummaryService } from './upload-summary/upload-summary.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formatName, sortByName } from 'app/shared/utils';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import {
  getFileNameValidationMessage,
  validateFileNames,
} from 'app/shared/utils/file-name-validation.util';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageNotification } from 'app/modules/models/messageNotification.type';
import { Subscription } from 'rxjs';
import { BroadcastService } from 'app/broadcast.service';
@Component({
  selector: 'app-creation-projet',
  templateUrl: './creation-projet.component.html',
  styleUrls: ['./creation-projet.component.scss']
})
export class CreationProjetComponent implements OnInit, OnDestroy {
  readonly signedCards = [
    'Devis',
    'Autres justificatifs (CDC, Etude de dimensionnement, etc.)'
  ];

  readonly preparationCards = [
    'Devis non signé',
    'Autres justificatifs'
  ];

  cards = [
    'Devis',
    'Autres justificatifs (CDC, Etude de dimensionnement, etc.)'
  ];

  creationMode: 'signed' | 'preparation' = 'signed';

  combinedFiles: { files: File[]; card: string }[] = [];
  fileValidationMessages: string[] = [];

  isUploading = false;
  projectStatus = '';

  // Entities
  company: Company;
  project: any;

  // Partners
  partner: any;
  selectedPartnerByMandate: any;
  selectedMandataire: any;
  partnersList: AcepActeur[];
  partnersCtrl = new FormControl();

  // Project form
  projectForm: FormGroup;

  // Map-related data
  options: FeatureCollection;
  filteredOptions: FeatureCollection;
  rawItems: any[] = [];
  private documentDraftProcessedSubscription?: Subscription;
  private documentDraftProgressSubscription?: Subscription;
  private pendingDraftProjectId: string | null = null;
  private pendingSubmissionContext: any | null = null;
  draftProgressPercent: number | null = null;
  draftProgressMessage: string | null = null;

  constructor(
    private readonly _storeService: StoreService,
    private readonly _partnerService: PartnersService,
    private readonly _dialog: MatDialog,
    private readonly _fb: FormBuilder,
    private readonly _buildingService: BuildingService,
    private readonly _creationProjetService: CreationProjetService,
    private readonly _uploadSummaryService: UploadSummaryService,
    private readonly _snackBar: MatSnackBar,
    private readonly _fuseConfirmationService: FuseConfirmationService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _broadcastService: BroadcastService,
  ) {
    this.projectForm = this._fb.group({
  rs: [''],
  nom: [''],
  prenom: [''],
  email: [''],
  tel: [''],
  dateEngagement: [''],
  dateTravauxPrevisionnelle: ['', Validators.required],
  adresse: [''],
  cp: [''],
  ville: [''],
  complement: [''],
  siret: ['']
    });
  }


  ngOnInit(): void {
    const mode = this._route.snapshot.queryParamMap.get('mode');
    this.projectStatus = (history.state?.projectStatus || this._route.snapshot.queryParamMap.get('projectStatus') || '').toString();
    this.creationMode = mode == 'preparation' ? 'preparation' : 'signed';
    this.cards = this.isPreparationMode ? this.preparationCards : this.signedCards;
    this.company = this._storeService.getCurrentCompany();
    this.partner = this._storeService.getUserPartner();
    this.selectedMandataire = localStorage.getItem('currentMandataire_localstorage') ? JSON.parse(localStorage.getItem('currentMandataire_localstorage')) : null;
    this.selectedPartnerByMandate = localStorage.getItem('currentPartner_localstorage') ? JSON.parse(localStorage.getItem('currentPartner_localstorage')) : null;
    if (this.selectedMandataire) {
      this.getPartnersByMandate()
    }
    this.getProjectHistory();

    this.documentDraftProcessedSubscription = this._broadcastService.documentDraftProcessed$.subscribe(notification => {
      this.handleDocumentDraftProcessedNotification(notification);
    });

    this.documentDraftProgressSubscription = this._broadcastService.documentDraftProgress$.subscribe(notification => {
      this.handleDocumentDraftProgressNotification(notification);
    });
  }

  ngOnDestroy(): void {
    this.documentDraftProcessedSubscription?.unsubscribe();
    this.documentDraftProgressSubscription?.unsubscribe();
  }

  private handleDocumentDraftProgressNotification(notification: MessageNotification): void {
    try {
      const content = notification?.content ? JSON.parse(notification.content) : null;
      if (!content) return;

      const projectId = content?.ProjectId || content?.projectId || null;
      const message = content?.message ?? content?.Message ?? null;
      const progress = Number(content?.progress ?? content?.Progress ?? null);

      if (projectId && this.pendingDraftProjectId && projectId === this.pendingDraftProjectId) {
        if (!isNaN(progress)) {
          this.draftProgressPercent = Math.max(0, Math.min(100, Math.round(progress)));
          this.draftProgressMessage = message ? String(message) : null;
        }
        // If progress is complete, ensure uploading flag is cleared (processed complete will also emit)
        if (this.draftProgressPercent === 100) {
          this.isUploading = false;
        }
      }
    } catch (e) {
      console.error('Failed to parse draft progress notification', e, notification?.content);
    }
  }

  getProjectHistory(): void {
    var partnerId = this.partner && this.partner?.guidId ? this.partner?.guidId : (this.partner?.id ? this.partner?.id : this.selectedPartnerByMandate?.id);
    var mandateId = this.selectedMandataire ? this.selectedMandataire.id : null;
    this._partnerService.getProjectHistory(partnerId, mandateId).subscribe({
      next: res => {
        this.rawItems = res;
      },
      error: _ => {
        this._snackBar.open('Une erreur serveur est survenue lors du chargement de l\'historique des projets !', null, {
          duration: 2000,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  onViewFile(card: any): void {
    if (card.file) {
      const fileURL = URL.createObjectURL(card.file);
      window.open(fileURL, '_blank');
    } else {
      alert(`Aucun fichier sélectionné pour "${card.label}".`);
    }
  }

  onFileDeleted(event: { file: File, card: string }) {
    const { file, card } = event;
    const index = this.combinedFiles.findIndex(f => f.card === card);

    if (index > -1) {
      this.combinedFiles[index].files = this.combinedFiles[index].files.filter(f => f !== file);

      // If no files remain, remove the entry entirely
      if (this.combinedFiles[index].files.length === 0) {
        this.combinedFiles.splice(index, 1);
      }

      this.refreshFileValidationState();
    }
  }


  onFileSelected(event: { files: File[], card: string }) {
    const { files, card } = event;
    const isMultiFile = card === 'Autres justificatifs (CDC, Etude de dimensionnement, etc.)' || card === 'Autres justificatifs';
    const currentSelectionNames = this.combinedFiles.flatMap(entry => entry.files.map(file => file.name));
    const validFiles = files.filter(file => !getFileNameValidationMessage(file.name, {}, currentSelectionNames));
    const invalidFileNames = files
      .filter(file => !!getFileNameValidationMessage(file.name, {}, currentSelectionNames))
      .map(file => file.name);

    if (invalidFileNames.length > 0) {
      this._snackBar.open(
        `Nom(s) de fichier invalide(s) : ${invalidFileNames.join(', ')}`,
        null,
        {
          duration: 3500,
          panelClass: 'warn-snackbar'
        }
      );
    }

    if (validFiles.length === 0) {
      this.refreshFileValidationState();
      return;
    }

    const index = this.combinedFiles.findIndex(f => f.card === card);

    if (isMultiFile) {
      if (index > -1) {
        // Avoid duplicates by checking file names
        const existing = this.combinedFiles[index].files.map(f => f.name);
        const newFiles = validFiles.filter(f => !existing.includes(f.name));
        this.combinedFiles[index].files.push(...newFiles);
      } else {
        this.combinedFiles.push({ card, files: [...validFiles] });
      }
    } else {
      // For single-file cards, always replace with the first file
      if (index > -1) {
        this.combinedFiles[index].files = [validFiles[0]];
      } else {
        this.combinedFiles.push({ card, files: [validFiles[0]] });
      }
    }

    this.refreshFileValidationState();
  }



  deposeProjectFinancement() {
    if (!this.isPreparationMode && this.projectForm.invalid) {
      this.projectForm.markAllAsTouched();
      this._snackBar.open('Veuillez renseigner la date de fin des travaux prévue.', null, {
        duration: 2000,
        panelClass: 'warn-snackbar'
      });
      return;
    }

    if (this.isUploadDisabled()) {
      this.refreshFileValidationState();
      return;
    }

    this.isUploading = true;
    const formData = this.buildProjectFilesFormData();
    if (!formData) {
      this.isUploading = false;
      return;
    }

    const submissionContext = this.getSubmissionContext();
    if (!submissionContext) {
      this.isUploading = false;
      this._snackBar.open('Impossible de récupérer le contexte partenaire pour le dépôt.', null, {
        duration: 2500,
        panelClass: 'warn-snackbar'
      });
      return;
    }

    const projectId = this.generateProjectId();
    formData.append('ProjectId', projectId);
    formData.append('CreationMode', this.creationMode);
    formData.append('IsPreparationProject', String(true));

    this._creationProjetService.depotDocumentProcessingDova(
      projectId,
      submissionContext?.userId,
      formData,
    ).subscribe({
      next: (_res: any) => {
        this.pendingDraftProjectId = projectId;
        this.pendingSubmissionContext = submissionContext;
        this._snackBar.open('Analyse du document en cours, veuillez patienter...', null, {
          duration: 2500,
          panelClass: 'accent-snackbar'
        });
      },
      error: _ => {
        this._snackBar.open('Erreur lors du dépôt du devis. Veuillez réessayer', null, {
          duration: 2000,
          panelClass: 'warn-snackbar'
        });
        this.isUploading = false;
      }
    });
    return;
  }

  /**
   * Returns the tooltip text for the submit button depending on form and upload state.
   */
  getSubmitTooltip(): string {
    if (this.projectForm.invalid) {
      return 'Veuillez remplir la date de fin des travaux';
    }

    if (this.isUploadDisabled()) {
      return 'Veuillez ajouter au moins un devis';
    }

    if (this.isUploading) {
      return 'Votre projet est en cours de dépôt, veuillez patienter...';
    }

    if (this.selectedMandataire && !this.selectedPartnerByMandate) {
      return 'Veuillez sélectionner un partenaire pour ce mandataire';
    }

    return '';
  }


  getSingleFileCard(): { files: File[], card: string }[] {
    const cardOrder = ['Devis', 'Devis non signé'];
    return this.combinedFiles
      .filter(f => !['Autres justificatifs (CDC, Etude de dimensionnement, etc.)', 'Autres justificatifs'].includes(f.card))
      .sort((a, b) => {
        const indexA = cardOrder.indexOf(a.card);
        const indexB = cardOrder.indexOf(b.card);
        return indexA - indexB;
      });

  }

  getMultipleFileCard(): { files: File[], card: string }[] {
    return this.combinedFiles.filter(f => ['Autres justificatifs (CDC, Etude de dimensionnement, etc.)', 'Autres justificatifs'].includes(f.card));
  }

  isUploadDisabled(): boolean {
    // In preparation mode, require that a 'Devis non signé' file is present.
    if (this.isPreparationMode) {
      const requiredCardsPrep = ['Devis non signé'];
      const missingRequiredFilesPrep = requiredCardsPrep.some(requiredCard => {
        const found = this.combinedFiles.find(f => f.card === requiredCard);
        return !found || found.files.length === 0;
      });

      return missingRequiredFilesPrep || this.hasFileValidationErrors();
    }

    const requiredCards = ['Devis'];

    const missingRequiredFiles = requiredCards.some(requiredCard => {
      const found = this.combinedFiles.find(f => f.card === requiredCard);
      return !found || found.files.length === 0;
    });

    return missingRequiredFiles || this.hasFileValidationErrors();
  }

  hasFileValidationErrors(): boolean {
    return this.fileValidationMessages.length > 0;
  }

  private buildProjectFilesFormData(): FormData | null {
    const formData = new FormData();
    const addedFileNames = new Set<string>();
    let otherFilesIndex = 0;

    for (const entry of this.combinedFiles) {
      const { card, files } = entry;

      for (const file of files) {
        const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.') || file.name;

        if (addedFileNames.has(nameWithoutExtension)) {
          this._fuseConfirmationService.open({
            title: 'Nom de fichier en double',
            message: `Le fichier "${nameWithoutExtension}" a déjà été ajouté. Veuillez renommer le fichier pour éviter les doublons.`,
          });
          return null;
        }

        addedFileNames.add(nameWithoutExtension);

        const name = file.name;
        const contentType = file.type;
        const extension = name.split('.').pop() || '';

        switch (card) {
          case 'Devis':
          case 'Devis non signé':
            formData.append('DevisFile.BlobFile', file);
            formData.append('DevisFile.Name', name);
            formData.append('DevisFile.ContentType', contentType);
            formData.append('DevisFile.Extension', extension);
            break;
          case 'Autres justificatifs (CDC, Etude de dimensionnement, etc.)':
          case 'Autres justificatifs':
            formData.append(`OtherFiles[${otherFilesIndex}].BlobFile`, file);
            formData.append(`OtherFiles[${otherFilesIndex}].Name`, name);
            formData.append(`OtherFiles[${otherFilesIndex}].ContentType`, contentType);
            formData.append(`OtherFiles[${otherFilesIndex}].Extension`, extension);
            otherFilesIndex++;
            break;
        }
      }
    }

    return formData;
  }

  /** Shared handler — call this both from the fake stub above and later from the SignalR BroadcastMessage case. */
  private _parsDovaResponse(dovaResponse: any, projectId: string, submissionContext: any, draftNotification?: MessageNotification, projectReference?: string | null): void {
    const missingFields: { label: string; status: string }[] = [];
    const dovaFiles: any[] = dovaResponse?.files || [];
    for (const dovaFile of dovaFiles) {
      for (const doc of (dovaFile.logicalDocuments || [])) {
        for (const field of (doc.fields || [])) {
          const isBooleanType = field.type?.toLowerCase() === 'boolean';
          if (field?.value === null || field?.value === undefined || field?.value === '' || (isBooleanType && (field?.value === false || field?.value?.toLowerCase() === "false"))) {
            missingFields.push({ label: field.label, status: 'Manquant' });
          }
        }
      }
    }

    this._uploadSummaryService.setFiles(this.combinedFiles);
    this._uploadSummaryService.setMissingFields(missingFields);
    this._router.navigate(['/project/documents-upload-summary'], {
      queryParams: { projectId },
      state: {
        combinedFiles: this.combinedFiles,
        projectForm: this.projectForm.value,
        projectStatus: 'En préparation',
        creationMode: this.creationMode,
        projectId,
        referenceProjet: projectReference || null,
        projectReference: projectReference || null,
        missingFields,
        submissionContext,
        draftProcessedNotification: draftNotification,
      }
    });
  }

  private createPreparationProjectAfterDova(dovaResponse: any, submissionContext: any, draftNotification?: MessageNotification): void {
    const formData = this.buildProjectFilesFormData();
    if (!formData) {
      this.isUploading = false;
      this.pendingDraftProjectId = null;
      this.pendingSubmissionContext = null;
      return;
    }

    this.projectForm.get('nom')?.setValue(formatName(this.projectForm.get('nom')?.value));
    this.projectForm.get('prenom')?.setValue(formatName(this.projectForm.get('prenom')?.value));
    this.projectForm.get('email')?.setValue(this.projectForm.get('email')?.value?.trim());
    this.projectForm.get('tel')?.setValue(this.projectForm.get('tel')?.value?.trim());
    this.projectForm.get('rs')?.setValue(this.projectForm.get('rs')?.value?.trim());
    this.projectForm.get('siret')?.setValue(submissionContext?.siretPartner?.trim());

    const beneficiaryInfoWithDovaAnalysis = {
      ...this.projectForm.value,
      DovaAnalysis: dovaResponse ? JSON.stringify(dovaResponse) : null,
    };

    formData.append('ProjectFinancementBeneficiaireInfo', JSON.stringify(beneficiaryInfoWithDovaAnalysis));
    formData.append('CreationMode', 'preparation');
    formData.append('IsPreparationProject', 'true');

    this._creationProjetService.createPrepareProject(
      submissionContext.companyId,
      submissionContext.partnerId,
      formData,
      submissionContext.mandateId,
      submissionContext.partnerName,
      submissionContext.mandateName,
      !this.isPreparationMode 
    ).subscribe({
      next: res => {
        this.isUploading = false;
        this.project = res;

        const createdProjectId = res?.projectId || res?.id || this.pendingDraftProjectId;
        const createdProjectReference = res?.projectReference || null;

        this._parsDovaResponse(dovaResponse, createdProjectId, submissionContext, draftNotification, createdProjectReference);

        this.pendingDraftProjectId = null;
        this.pendingSubmissionContext = null;
      },
      error: () => {
        this.isUploading = false;
        this.pendingDraftProjectId = null;
        this.pendingSubmissionContext = null;
        this._snackBar.open('Erreur lors de la creation du projet. Veuillez reessayer', null, {
          duration: 2500,
          panelClass: 'warn-snackbar'
        });
      }
    });
  }

  private handleDocumentDraftProcessedNotification(notification: MessageNotification): void {
    if (!this.pendingDraftProjectId || !this.pendingSubmissionContext) {
      return;
    }

    let parsedContent: any = null;
    try {
      parsedContent = notification?.content ? JSON.parse(notification.content) : null;
    } catch {
      return;
    }

    if (!parsedContent?.projectId || parsedContent.projectId !== this.pendingDraftProjectId) {
      return;
    }

    // Ensure progress is shown as complete before continuing
    this.draftProgressPercent = 100;
    setTimeout(() => {
      this.createPreparationProjectAfterDova(parsedContent, this.pendingSubmissionContext, notification);
      this.pendingDraftProjectId = null;
      this.pendingSubmissionContext = null;
      this.isUploading = false;
    }, 120);
  }

  private getSubmissionContext(): any | null {
    const company = this.company;
    const connectedUser = this._storeService.getConnectedUser();
    let partnerId = this.partner && this.partner?.guidId ? this.partner?.guidId : (this.partner?.id ? this.partner?.id : this.selectedPartnerByMandate?.id);
    const mandateId = this.selectedMandataire ? this.selectedMandataire.id : null;
    let partnerName = this.selectedPartnerByMandate ? this.selectedPartnerByMandate.name : this.partner?.nom;
    const mandateName = this.selectedMandataire ? this.selectedMandataire.name : null;
    let siretPartner = this.selectedPartnerByMandate ? this.selectedPartnerByMandate.siret : this.partner?.siret;
    const userId = connectedUser?.guidId || connectedUser?.id || connectedUser?.userId || null;

    if (this.partnersCtrl?.value) {
      partnerId = this.partnersCtrl.value.id;
      partnerName = this.partnersCtrl.value.name;
      siretPartner = this.partnersCtrl.value.siret;
    }

    if (!company?.id || !partnerId) {
      return null;
    }

    return {
      companyId: company.id,
      userId,
      partnerId,
      mandateId,
      partnerName,
      mandateName,
      siretPartner,
      isPreparationMode: this.isPreparationMode,
    };
  }

  private generateProjectId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  private refreshFileValidationState(): void {
    const validation = validateFileNames(
      this.combinedFiles.flatMap(entry => entry.files.map(file => file.name))
    );

    this.fileValidationMessages = validation.messages;
  }

  getPartnersByMandate() {
    this._partnerService.getPartnersByMandate(this.selectedMandataire.id).subscribe(partners => {
      this.partnersList = [
        ...new Map(partners.map(p => [p.id, p])).values()
      ];
      this.partnersList = [...this.partnersList.filter(p => p.isActive)];
      this.partnersList = sortByName(this.partnersList);
      if (this.selectedPartnerByMandate) {
        this.partnersCtrl.setValue(this.partnersList.find(p => p.id === this.selectedPartnerByMandate.id));
      }
    });
  }

  onPartnerSelected(event: any) {
    if (event?.value) {
      this.selectedPartnerByMandate = event.value;
      this.getProjectHistory();
    }
  }

  showAdressAutocomplete() {
    let dialog = this._dialog.open(AddressAutocompleteComponent, {
      width: '600px',
      data: this.options,
    });

    dialog.afterClosed().subscribe((result: any) => {
      if (result) {
        this.splitAddress(result.option.value);
      }
    });
  }

  subscribeToAddressChanges() {
    this.projectForm.get('address').valueChanges.pipe(debounceTime(1000)).subscribe(value => {
      if (value !== null && value !== undefined && value !== '') {
        this._buildingService.getAddress(value).subscribe((result: FeatureCollection) => {
          this.options = result;
          this.filteredOptions = this.options;
        });
      }
    });
  }

  splitAddress(mainAddress: AddressFeature) {
    if (mainAddress) {
      let zipCode = mainAddress.properties.postcode;
      let name = mainAddress?.properties?.name ? mainAddress.properties.name : "";
      let town: string = mainAddress?.properties?.city || mainAddress.properties.district || "";

      this.projectForm.get('adresse')?.setValue(name);
      this.projectForm.get('ville')?.setValue(town);
      this.projectForm.get('cp')?.setValue(zipCode);
    }
  }

  selectedOption(event: any) {
    const selectedAddress: AddressFeature = event.option.value;
    this.splitAddress(selectedAddress);
  }

  saveProject(company: any, partnerId: any, mandateId: any, formData: any, partnerName: any, mandateName: any, projectFormValue: FormGroup, siretPartner: string) {
    projectFormValue.get('nom')!.setValue(
      formatName(projectFormValue.get('nom')?.value)
    );

    projectFormValue.get('prenom')!.setValue(
      formatName(projectFormValue.get('prenom')?.value)
    );

    projectFormValue.get('email').setValue(projectFormValue.get('email').value?.trim());
    projectFormValue.get('tel').setValue(projectFormValue.get('tel').value?.trim());
    projectFormValue.get('rs').setValue(projectFormValue.get('rs').value?.trim());
    projectFormValue.get('siret').setValue(siretPartner?.trim());
    formData.append('ProjectFinancementBeneficiaireInfo', projectFormValue.value ? JSON.stringify(projectFormValue.value) : '');

    this._creationProjetService.depotProjectFinancement(
      company.id,
      partnerId,
      formData,
      mandateId,
    ).subscribe({
      next: res => {
        this.project = res;
        const projectId = res?.projectId || res?.id;
        this.resetAll();
        this.getProjectHistory();
        this._snackBar.open(`Votre projet a bien été créé avec la référence : ${res.projectReference}`, null, { duration: 2000, panelClass: 'accent-snackbar' });

        if (projectId) {
          this._router.navigate(['/fiche-projet-avant-travaux/' + projectId]);
        }
        // Redirect to the upload summary page after successful upload
        //this._router.navigate(['project/documents-upload-summary', res.projectId], { state: { projectReference: res.projectReference } });
      },
      error: err => {
        this._snackBar.open('Erreur lors de la création. Veuillez réessayer', null, { duration: 2000, panelClass: 'warn-snackbar' });
        this.isUploading = false;
      },
    });
  }

  // Method to reset form, files, and partners
  resetAll() {
    // Reset form
    this.projectForm.reset();
    // Clear uploaded files
    this.combinedFiles = [];
    this.isUploading = false;
  }

  get isPreparationMode(): boolean {
    return this.creationMode === 'preparation';
  }

  goToDossiersProjet(): void {
    this._router.navigate(['/partner-dossier-project']);
  }
}
